import * as React from 'react';
import { Link } from 'react-router-dom';
import { startCase, camelCase } from 'lodash';

import { PaddedSection, UploadAndCheckTableFiltersBar } from 'common/components';
import { generateColumns, addReferenceSuffixToBenfitOutcome } from 'common/helpers/utils';
import { FilterData } from 'common/helpers/url';
import { PaginatedTable } from 'features/pagination/components/PaginatedTable';
import { PaginationView, AutocompleteField } from 'features/pagination/models/paginationModel';
import { Antd3Form, Antd3FormProps } from 'common/components/deprecated/antd3';

import { UploadBenefit } from '../models/uploadModels';
import { ApprovedUploadProps as ApprovedUploadContainerProps } from '../containers/ApprovedUploadContainer';
import { getReadyForEvidenceBenefitsSuccess } from '../actions/evidenceActions';
import { generateUploadBenefitEvidencePath } from '../config/uploadRouting';

const uploadEvidenceTableColumns = [
    {
        title: 'Benefit',
        key: 'outcome',

        render: (outcome: string, { organisation, contract, id }: UploadBenefit) => (
            <Link to={generateUploadBenefitEvidencePath(organisation, contract, id)}>
                {outcome}
            </Link>
        ),
    },
    {
        title: 'Benefit Label',
        key: 'label',
        render: (label: string, { organisation, contract, id }: UploadBenefit) => (
            <Link to={generateUploadBenefitEvidencePath(organisation, contract, id)}>{label}</Link>
        ),
    },
    {
        title: 'Project',
        key: 'contractTitle',
        render: (contractTitle: string) => contractTitle,
    },
    {
        title: 'Ref. No.',
        key: 'contractReferenceNumber',
    },
    {
        title: 'Quantity',
        key: 'quantity',
    },
    {
        title: 'Status',
        key: 'status',
        render: (status: string) => startCase(camelCase(status)),
    },
];

type ApprovedUploadEvidencesProps = Antd3FormProps & ApprovedUploadContainerProps;

class ApprovedUploadEvidencesForm extends React.Component<ApprovedUploadEvidencesProps> {
    private refreshTable = (filters?: FilterData) =>
        this.props.paginationRequest({
            view: PaginationView.ApprovedUploadEvidence,
            pagination: {
                current: 1,
            },
            filters,
            paginationSuccessCallback: getReadyForEvidenceBenefitsSuccess,
        });

    private saveBenefitFilters = (benefitReferenceNumber: string | undefined) =>
        this.props.saveFilters({
            paginatedView: PaginationView.ApprovedUploadEvidence,
            values: {
                referenceNumber: benefitReferenceNumber,
            },
        });

    private saveContractFilters = (contractId: number | undefined) =>
        this.props.saveFilters({
            paginatedView: PaginationView.ApprovedUploadEvidence,
            values: {
                contract: contractId,
            },
        });

    private saveBuyerOrganisationFilter = (tenant: string | undefined) => {
        this.props.saveFilters({
            paginatedView: PaginationView.ApprovedUploadScores,
            values: { tenant },
        });
    };

    private searchByBenefits = (benefitReferenceNumber: string) =>
        this.saveBenefitFilters(benefitReferenceNumber) && this.refreshTable();

    private searchByContracts = (contractId: number) =>
        this.saveContractFilters(contractId) && this.refreshTable();

    private searchByBuyerOrganisation = (buyer: string) => {
        this.saveBuyerOrganisationFilter(buyer);
        this.refreshTable();
    };

    private onRemoveBenefitAutocomplete = () =>
        this.saveBenefitFilters(undefined) && this.refreshTable();

    private onRemoveContractAutocomplete = () =>
        this.saveContractFilters(undefined) && this.refreshTable();

    private onRemoveBuyerOrganisationAutocomplete = () => {
        this.saveBuyerOrganisationFilter(undefined);
        this.refreshTable();
    };

    private clearFilters = () =>
        this.props.clearFilters(PaginationView.ApprovedUploadEvidence) &&
        this.refreshTable() &&
        this.props.form.setFieldsValue({
            quickFilterBenefitReferenceNumber: '',
            quickFilterContractReferenceNumber: '',
        });

    public render(): JSX.Element {
        const { form, filter, approvedEvidences, isTenant } = this.props;

        return (
            <PaddedSection header="Submit Approved Evidence">
                <UploadAndCheckTableFiltersBar
                    form={form}
                    searchByBenefits={this.searchByBenefits}
                    onRemoveBenefitAutocomplete={this.onRemoveBenefitAutocomplete}
                    searchByContracts={this.searchByContracts}
                    onRemoveContractAutocomplete={this.onRemoveContractAutocomplete}
                    searchByBuyerOrganisation={this.searchByBuyerOrganisation}
                    onRemoveBuyerOrganisationAutocomplete={
                        this.onRemoveBuyerOrganisationAutocomplete
                    }
                    filter={filter}
                    clearFilters={this.clearFilters}
                    benefitFilterAutocompleteField={AutocompleteField.ApprovedUploadEvidences}
                    isTenant={isTenant}
                />
                <PaginatedTable
                    view={PaginationView.ApprovedUploadEvidence}
                    data={addReferenceSuffixToBenfitOutcome(approvedEvidences)}
                    columns={generateColumns(uploadEvidenceTableColumns)}
                    emptyText="No benefits have been assigned to you"
                    paginationSuccessCallback={getReadyForEvidenceBenefitsSuccess}
                />
            </PaddedSection>
        );
    }
}

export const ApprovedUploadEvidences = Antd3Form.create({})(ApprovedUploadEvidencesForm);
